import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, apiFetch } from '../api';
import { useMessageToast } from '../useMessageToast';

type AgentItem = {
  id: string;
  title: string;
  description: string;
  tags: string[];
  botId: string;
};

type ChatMsg = { role: 'user' | 'assistant'; text: string };

type CozeConfig = { botGeneral: string; botMbti: string; botTarot: string };

function storageKey(botId: string) {
  return `coze_conversation_${botId}`;
}

function safeParseJson(raw: string) {
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch {
    return { ok: false, value: null };
  }
}

export function AgentsPage() {
  const agents: AgentItem[] = useMemo(
    () => [
      {
        id: 'general',
        title: '通用助手',
        description: '适合日常问答、总结改写、结构化输出。',
        tags: ['通用', '写作', '总结'],
        botId: ''
      },
      {
        id: 'mbti',
        title: 'MBTI 深度解读',
        description: '输入 MBTI 结果或倾向描述，输出更结构化的建议与提醒。',
        tags: ['MBTI', '解读', '建议'],
        botId: ''
      },
      {
        id: 'tarot',
        title: '塔罗解读助手',
        description: '输入问题与牌阵信息，输出分段解读与行动建议。',
        tags: ['塔罗', '解读'],
        botId: ''
      }
    ],
    []
  );

  const [selectedId, setSelectedId] = useState(agents[0]?.id || '');
  const selected = agents.find((a) => a.id === selectedId) || agents[0];

  const [botId, setBotId] = useState(selected?.botId || '');
  const [cozeCfg, setCozeCfg] = useState<CozeConfig | null>(null);
  const [mode, setMode] = useState<'chat' | 'shortcut'>('chat');
  const [prompt, setPrompt] = useState('');
  const [commandId, setCommandId] = useState('');
  const [commandParams, setCommandParams] = useState('{\n  \"param1\": \"[{\\\\\"type\\\\\":\\\\\"text\\\\\",\\\\\"text\\\\\":\\\\\"参数值1\\\\\"}]\"\n}');
  const [conversationId, setConversationId] = useState('');
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  useMessageToast(msg, setMsg, 'agentspage');
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const botFromConfig = (agentId: string, cfg: CozeConfig | null) => {
    if (!cfg) return '';
    if (agentId === 'general') return cfg.botGeneral || '';
    if (agentId === 'mbti') return cfg.botMbti || '';
    if (agentId === 'tarot') return cfg.botTarot || '';
    return '';
  };

  useEffect(() => {
    apiFetch<{ ok: boolean; config: CozeConfig }>('/api/user/coze-config', { method: 'GET' })
      .then((r) => setCozeCfg(r.config))
      .catch(() => setCozeCfg({ botGeneral: '', botMbti: '', botTarot: '' }));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const b = botFromConfig(selectedId, cozeCfg);
    if (b && !String(botId || '').trim()) setBotId(b);
  }, [cozeCfg, selectedId]);

  useEffect(() => {
    const b = String(botId || '').trim();
    if (!b) {
      setConversationId('');
      return;
    }
    try {
      const saved = String(localStorage.getItem(storageKey(b)) || '').trim();
      setConversationId(saved);
    } catch {
      setConversationId('');
    }
  }, [botId]);

  const canRun =
    !loading &&
    !!botId.trim() &&
    (mode === 'chat' ? !!prompt.trim() : !!commandId.trim());

  return (
    <div className="container">
      <div className="card">
        <div className="card-inner">
          <div className="section-title">
            <h2>智能体</h2>
            <span className="muted">通过 Coze 智能体为工具解读与内容生成提供能力</span>
          </div>

          <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            {agents.map((a) => (
              <button
                key={a.id}
                className={`pill ${a.id === selectedId ? 'active' : ''}`}
                onClick={() => {
                  setSelectedId(a.id);
                  setBotId(botFromConfig(a.id, cozeCfg) || a.botId || '');
                  setMessages([]);
                  setMsg(null);
                }}
                type="button"
              >
                {a.title}
              </button>
            ))}
          </div>

          {selected ? (
            <div className="tool" style={{ marginBottom: 12 }}>
              <div className="tool-top">
                <div className="tool-name">{selected.title}</div>
                <span className="badge">多轮会话</span>
              </div>
              <div className="muted" style={{ lineHeight: 1.8 }}>
                <div>{selected.description}</div>
                <div style={{ marginTop: 8 }}>
                  {selected.tags.map((t) => (
                    <span key={t} className="pill" style={{ marginRight: 8 }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <div className="split">
            <div>
              <div className="field">
                <div className="muted">Bot ID</div>
                <input value={botId} onChange={(e) => setBotId(e.target.value)} placeholder="填写 Coze bot_id" />
              </div>

              <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
                <button className={`pill ${mode === 'chat' ? 'active' : ''}`} onClick={() => setMode('chat')} type="button">
                  对话
                </button>
                <button className={`pill ${mode === 'shortcut' ? 'active' : ''}`} onClick={() => setMode('shortcut')} type="button">
                  快捷指令
                </button>
              </div>

              {mode === 'chat' ? (
                <div className="field" style={{ marginTop: 12 }}>
                  <div className="muted">输入</div>
                  <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="输入你想让智能体完成的任务" />
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                  <div className="field">
                    <div className="muted">Command ID</div>
                    <input value={commandId} onChange={(e) => setCommandId(e.target.value)} placeholder="填写 Coze shortcut command_id" />
                  </div>
                  <div className="field">
                    <div className="muted">Parameters（JSON，可选）</div>
                    <textarea value={commandParams} onChange={(e) => setCommandParams(e.target.value)} placeholder="{ }" />
                  </div>
                </div>
              )}

              <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
                当前会话：{conversationId ? conversationId : '未建立'}
              </div>

              <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="btn"
                  disabled={!canRun}
                  onClick={async () => {
                    const b = botId.trim();
                    const cid = conversationId.trim();
                    const p = prompt.trim();
                    const cmd = commandId.trim();
                    if (!b) return;
                    if (mode === 'chat' && !p) return;
                    if (mode === 'shortcut' && !cmd) return;

                    const paramsObj =
                      mode === 'shortcut' && commandParams.trim()
                        ? (() => {
                            const parsed = safeParseJson(commandParams);
                            return parsed.ok && parsed.value && typeof parsed.value === 'object' ? parsed.value : null;
                          })()
                        : null;
                    if (mode === 'shortcut' && commandParams.trim() && paramsObj == null) {
                      setMsg('参数 JSON 格式不正确');
                      return;
                    }

                    setLoading(true);
                    setMsg(null);
                    abortRef.current?.abort();
                    const controller = new AbortController();
                    abortRef.current = controller;

                    let nextMessages = messages;
                    if (mode === 'chat') {
                      nextMessages = [...messages, { role: 'user', text: p }, { role: 'assistant', text: '' }];
                      setMessages(nextMessages);
                      setPrompt('');
                    } else {
                      nextMessages = [...messages, { role: 'user', text: `执行快捷指令：${cmd}` }, { role: 'assistant', text: '' }];
                      setMessages(nextMessages);
                    }

                    try {
                      const resp = await fetch('/api/agents/coze/chat/stream', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify(
                          mode === 'shortcut'
                            ? {
                                botId: b,
                                conversationId: cid || undefined,
                                mode: 'shortcut',
                                shortcutCommand: { commandId: cmd, parameters: paramsObj || undefined }
                              }
                            : { botId: b, prompt: p, conversationId: cid || undefined, mode: 'chat' }
                        ),
                        signal: controller.signal
                      });

                      if (!resp.ok) {
                        let body: any = null;
                        try {
                          body = await resp.json();
                        } catch {
                        }
                        throw new ApiError(resp.status, body);
                      }

                      if (!resp.body) throw new ApiError(502, { error: 'COZE_REQUEST_FAILED' });

                      const reader = resp.body.getReader();
                      const decoder = new TextDecoder();
                      let buf = '';
                      let receivedMeta = false;

                      while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        buf += decoder.decode(value, { stream: true });
                        while (true) {
                          const idx = buf.indexOf('\n');
                          if (idx < 0) break;
                          const line = buf.slice(0, idx).trim();
                          buf = buf.slice(idx + 1);
                          if (!line) continue;
                          const parsed = safeParseJson(line);
                          if (!parsed.ok) continue;
                          const payload = parsed.value;
                          if (payload?.type === 'meta' && typeof payload?.conversationId === 'string') {
                            receivedMeta = true;
                            const nextCid = String(payload.conversationId || '').trim();
                            if (nextCid) {
                              setConversationId(nextCid);
                              try {
                                localStorage.setItem(storageKey(b), nextCid);
                              } catch {
                              }
                            }
                            continue;
                          }
                          if (payload?.type === 'delta' && typeof payload?.text === 'string') {
                            const delta = payload.text;
                            setMessages((prev) => {
                              if (!prev.length) return prev;
                              const copy = [...prev];
                              const last = copy[copy.length - 1];
                              if (last.role !== 'assistant') return prev;
                              copy[copy.length - 1] = { ...last, text: (last.text || '') + delta };
                              return copy;
                            });
                            continue;
                          }
                          if (payload?.type === 'error') {
                            throw new ApiError(502, { error: payload.error || 'COZE_REQUEST_FAILED' });
                          }
                          if (payload?.type === 'done') {
                            if (!receivedMeta && typeof payload?.conversationId === 'string') {
                              const nextCid = String(payload.conversationId || '').trim();
                              if (nextCid) {
                                setConversationId(nextCid);
                                try {
                                  localStorage.setItem(storageKey(b), nextCid);
                                } catch {
                                }
                              }
                            }
                          }
                        }
                      }

                      setMsg('执行成功');
                    } catch (e) {
                      if (e instanceof ApiError) {
                        const code = (e.body as any)?.error;
                        if (code === 'COZE_NOT_CONFIGURED') setMsg('当前环境未配置 Coze Token');
                        else if (code === 'PROMPT_TOO_LONG') setMsg('输入过长，请缩短后重试');
                        else if (code === 'COMMAND_ID_REQUIRED') setMsg('缺少 Command ID');
                        else if (code === 'COZE_REQUEST_FAILED') setMsg('调用失败，请稍后重试');
                        else setMsg('执行失败，请稍后重试');
                      } else {
                        setMsg('执行失败');
                      }
                    } finally {
                      setLoading(false);
                    }
                  }}
                  type="button"
                >
                  {loading ? '执行中…' : '发送'}
                </button>
                <button
                  className="btn secondary"
                  disabled={!loading}
                  onClick={() => {
                    abortRef.current?.abort();
                    setLoading(false);
                    setMsg('已停止');
                  }}
                  type="button"
                >
                  停止
                </button>
                <button
                  className="btn secondary"
                  onClick={() => {
                    setPrompt('');
                    setMessages([]);
                    setMsg(null);
                  }}
                  type="button"
                >
                  清空
                </button>
                <button
                  className="btn secondary"
                  disabled={!botId.trim()}
                  onClick={() => {
                    const b = botId.trim();
                    setConversationId('');
                    setMessages([]);
                    setMsg('已开启新会话');
                    try {
                      localStorage.removeItem(storageKey(b));
                    } catch {
                    }
                  }}
                  type="button"
                >
                  新会话
                </button>
              </div>
              <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
                - 已支持流式打字机效果与多轮会话（conversation_id 保存在本机）
                <br />- 为安全起见，Coze Token 仅保存在服务端
              </div>
            </div>

            <div>
              <div className="tool">
                <div className="tool-top">
                  <div className="tool-name">对话</div>
                  <span className="badge">只读</span>
                </div>
                <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                  {messages.length ? (
                    messages.map((m, idx) => (
                      <div
                        key={`${idx}-${m.role}`}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 14,
                          border: '1px solid rgba(255,255,255,0.12)',
                          background: m.role === 'assistant' ? 'rgba(255,255,255,0.06)' : 'rgba(139,92,246,0.10)'
                        }}
                      >
                        <div className="muted" style={{ fontWeight: 900, marginBottom: 6 }}>
                          {m.role === 'assistant' ? '智能体' : '你'}
                        </div>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>{m.text || '…'}</pre>
                      </div>
                    ))
                  ) : (
                    <div className="muted">暂无消息</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

